import { checkSession } from "@features/auth";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { preloadQuery } from "@relay";
import { useAsyncMutation } from "@hooks/useAsyncMutation";
import { Button } from "@shadcn/ui/button";
import { createFileRoute, Navigate, redirect, useRouter } from "@tanstack/react-router";
import { LoaderCircle } from "lucide-react";
import { Suspense, useState } from "react";
import { toast } from "sonner";
import { HubMap } from "~/components/layout/HubMap/HubMap";
import { usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { AppHeader } from "~/components/layout/AppHeader";
import { BackLink } from "~/components/one-delivery/BackLink";
import { PageContainer } from "~/components/layout/PageContainer";
import { requireOnboarded } from "~/components/one-delivery/onboarding";
import { ShippingAddress } from "~/components/one-delivery/ShippingAddress";
import type { newShipmentOneDeliveryHubSaveMutation } from "./__generated__/newShipmentOneDeliveryHubSaveMutation.graphql";
import type { newShipmentOneDeliveryQuery } from "./__generated__/newShipmentOneDeliveryQuery.graphql";

const query = graphql`
	query newShipmentOneDeliveryQuery {
		onedeliveryProfile {
			...ShippingAddressFragment
		}
	}
`;

const saveMutation = graphql`
	mutation newShipmentOneDeliveryHubSaveMutation($hubId: UUID!) {
		onedeliveryProfileSave(input: { preferredHubId: $hubId }) {
			userId
		}
	}
`;

export const Route = createFileRoute("/one-delivery/new-shipment")({
	beforeLoad: () => {
		document.title = "bacehub | One Delivery";
	},
	component: RouteComponent,
	loader: async ({ context }) => {
		if (!(await checkSession())) {
			throw redirect({ to: "/auth", search: { redirectToPath: "/one-delivery/new-shipment", show: "sign-in" } });
		}
		await requireOnboarded(context.relayEnvironment);
		return preloadQuery<newShipmentOneDeliveryQuery>(context.relayEnvironment, query, {}, { fetchPolicy: "network-only" });
	},
});

/** How to ship a new order: the checkout address of the preferred Hub's warehouse with the bace-ID. */
function RouteComponent() {
	const i18n = useI18nContext().LL["new-ui"];
	const router = useRouter();
	const { onedeliveryProfile: profile } = usePreloadedQuery<newShipmentOneDeliveryQuery>(query, Route.useLoaderData());
	const [hubMapOpen, setHubMapOpen] = useState(false);
	const [save, isSaving] = useAsyncMutation<newShipmentOneDeliveryHubSaveMutation>(saveMutation);

	/** Changes the preferred Hub, then reloads the address of the warehouse serving it. */
	const changeHub = async (hubId: string, close: () => void) => {
		try {
			await save({ variables: { hubId } });
			close();
			await router.invalidate();
			toast.success(i18n.OD_SHIP_HUB_CHANGED());
		} catch (error) {
			toast.error(i18n.OD_ONB_HUB_SAVE_FAILED(), { description: error instanceof Error ? error.message : undefined });
		}
	};
	// The address depends on the preferred Hub, chosen in the onboarding.
	if (!profile) return <Navigate to="/one-delivery/onboarding/pricing" replace />;

	return (
		<PageContainer className="min-h-dvh bg-slate-50">
			<AppHeader />
			<BackLink to="/one-delivery" label={i18n.OD_BACK_TO_SHIPMENTS()} />
			<div className="flex flex-col gap-1 px-6 pt-4">
				<h1 className="font-breit text-2xl font-normal text-neutral-900">
					<T>{i18n.OD_NEW_SHIPMENT()}</T>
				</h1>
			</div>
			<div className="px-6 pb-8">
				<ShippingAddress profile={profile} onChangeHub={() => setHubMapOpen(true)} />
			</div>
			{hubMapOpen && (
				<Suspense fallback={null}>
					<HubMap
						open={hubMapOpen}
						onOpenChange={setHubMapOpen}
						filterService="ONEDELIVERY"
						renderAction={({ id }, close) => (
							<Button className="w-full font-breit font-normal" disabled={isSaving} onClick={() => void changeHub(id, close)}>
								{isSaving && <LoaderCircle className="size-4 animate-spin" />}
								{i18n.CHOOSE_HUB()}
							</Button>
						)}
					/>
				</Suspense>
			)}
		</PageContainer>
	);
}
